'use client';

import { useEffect, useState } from 'react';
import Image from '@/components/compat/Image';
import { LineChart, List, Loader2, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DiscoverDialogClose } from '@/components/help-center/DiscoverDialogClose';
import { MoXat, cn } from '@/components/help-center/latest-power-shared';
import { DaysStarIcon } from '@/components/help-center/fairtrade/fairtrade-shared';
import { type PowerInfo, formatFairtradePrice } from '@/lib/powers-data';
import {
  fetchPowerFairtradeHistory,
  formatFairtradeLogDate,
  formatFairtradeRangeLabel,
  getFairtradeChangeDirection,
  type FairtradeChartPoint,
  type FairtradeCurrentQuote,
  type FairtradeHistoryQuoteHint,
  type FairtradePriceLogEntry,
  type FairtradePriceRange,
} from '@/lib/fairtrade-history-data';
import { POWER_TRADE_ICON_SRC } from '@/components/help-center/powers/power-shared';
import FairtradeHistoryCharts from '@/components/help-center/powers/FairtradeHistoryCharts';

/** PowerInfo completo ou sujeito mínimo (lista Fairtrade). */
export type FairtradeHistorySubject = {
  slug: string;
  name: string;
  prices?: {
    fairtrade_min: number;
    fairtrade_max: number;
    fairtrade_min_days?: number;
    fairtrade_max_days?: number;
  };
};

function ChangeIcon({
  direction,
}: {
  direction: ReturnType<typeof getFairtradeChangeDirection>;
}) {
  if (direction === 'up') {
    return <TrendingUp className="h-3.5 w-3.5 text-emerald-400" aria-hidden />;
  }
  if (direction === 'down') {
    return <TrendingDown className="h-3.5 w-3.5 text-rose-400" aria-hidden />;
  }
  if (direction === 'same') {
    return <Minus className="h-3.5 w-3.5 text-sky-300/70" aria-hidden />;
  }
  return null;
}

function RangeBlock({
  label,
  xats,
  days,
  emphasize = false,
}: {
  label: string;
  xats: FairtradePriceRange | null;
  days: FairtradePriceRange | null;
  emphasize?: boolean;
}) {
  return (
    <div>
      <p className="font-poppins text-[10px] font-semibold uppercase tracking-wide text-white/45">
        {label}
      </p>
      <div className="mt-1 flex flex-col items-start gap-1.5">
        <span
          className={cn(
            'flex items-center gap-1.5 font-poppins text-sm tabular-nums',
            emphasize ? 'font-semibold text-white' : 'text-blue-100/85'
          )}
        >
          <MoXat className="h-3.5 w-3.5 shrink-0" />
          {formatFairtradeRangeLabel(xats, 'xats')}
        </span>
        {days ? (
          <span
            className={cn(
              'flex items-center gap-1.5 font-poppins text-sm tabular-nums',
              emphasize ? 'font-semibold text-amber-100' : 'text-amber-100/80'
            )}
          >
            <DaysStarIcon className="h-3.5 w-3.5 shrink-0" />
            {formatFairtradeRangeLabel(days, 'days')}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function HistoryRow({ entry }: { entry: FairtradePriceLogEntry }) {
  const direction = getFairtradeChangeDirection(entry.oldRange, entry.newRange);

  return (
    <li
      className={cn(
        'rounded-xl border border-white/[0.08] bg-gradient-to-br from-white/[0.06] to-white/[0.02]',
        'px-3.5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
        'transition-[border-color,background-color] duration-200 ease-out',
        'hover:border-white/[0.14] hover:from-white/[0.08] hover:to-white/[0.03]'
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <time
          className="font-poppins text-[11px] font-semibold uppercase tracking-wide text-sky-200/80"
          dateTime={entry.date}
        >
          {formatFairtradeLogDate(entry.date)}
        </time>
        <ChangeIcon direction={direction} />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <RangeBlock label="Anterior" xats={entry.oldRange} days={entry.oldDaysRange} />
        <RangeBlock
          label="Novo"
          xats={entry.newRange}
          days={entry.newDaysRange}
          emphasize
        />
      </div>
    </li>
  );
}

function CurrentQuoteCard({ quote }: { quote: FairtradeCurrentQuote }) {
  const hasXats = quote.minXats > 0 || quote.maxXats > 0;
  const hasDays = quote.minDays > 0 || quote.maxDays > 0;
  if (!hasXats && !hasDays) return null;

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] px-3.5 py-2.5">
      <p className="font-poppins text-[10px] font-semibold uppercase tracking-wide text-sky-200/75">
        Preço atual
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {hasXats ? (
          <span className="inline-flex items-center gap-1.5 font-poppins text-sm font-semibold tabular-nums text-white">
            <MoXat className="h-3.5 w-3.5 shrink-0" />
            {formatFairtradePrice(quote.minXats, quote.maxXats)}
          </span>
        ) : null}
        {hasDays ? (
          <span className="inline-flex items-center gap-1.5 font-poppins text-sm font-semibold tabular-nums text-amber-100">
            <DaysStarIcon className="h-3.5 w-3.5 shrink-0" />
            {formatFairtradeRangeLabel(
              { min: quote.minDays || quote.maxDays, max: quote.maxDays || quote.minDays },
              'days'
            )}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function subjectToHint(power: PowerInfo | FairtradeHistorySubject): FairtradeHistoryQuoteHint {
  const prices = power.prices;
  if (!prices) return {};
  return {
    minXats: prices.fairtrade_min,
    maxXats: prices.fairtrade_max,
    minDays:
      'fairtrade_min_days' in prices ? Number(prices.fairtrade_min_days) || 0 : 0,
    maxDays:
      'fairtrade_max_days' in prices ? Number(prices.fairtrade_max_days) || 0 : 0,
  };
}

export default function PowerFairtradeHistoryDialog({
  power,
  triggerClassName,
}: {
  power: PowerInfo | FairtradeHistorySubject;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'registros' | 'graficos'>('registros');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<FairtradePriceLogEntry[]>([]);
  const [chart, setChart] = useState<FairtradeChartPoint[]>([]);
  const [quote, setQuote] = useState<FairtradeCurrentQuote | null>(null);
  const [daysEstimated, setDaysEstimated] = useState(false);

  const slug = power.slug;
  const displayName = power.name;
  const hint = subjectToHint(power);

  useEffect(() => {
    if (!open) {
      setTab('registros');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPowerFairtradeHistory(slug, hint)
      .then((data) => {
        if (cancelled) return;
        setLogs(data.logs);
        setChart(data.chart);
        setQuote(data.quote);
        setDaysEstimated(data.daysEstimated);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Não foi possível carregar o histórico de preços.');
        setLogs([]);
        setChart([]);
        setQuote(null);
        setDaysEstimated(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slug, hint.minXats, hint.maxXats, hint.minDays, hint.maxDays]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
            'border border-sky-400/25 bg-sky-500/10 text-sky-200/90',
            'transition-colors hover:border-sky-300/40 hover:bg-sky-400/20 hover:text-sky-100',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50',
            triggerClassName
          )}
          aria-label={`Ver histórico de preços fairtrade de ${displayName}`}
          title="Histórico fairtrade"
          onClick={(e) => e.stopPropagation()}
        >
          <LineChart className="h-3.5 w-3.5" aria-hidden />
        </button>
      </DialogTrigger>

      <DialogContent
        showCloseButton={false}
        className={cn(
          'flex max-h-[min(92vh,860px)] max-w-xl flex-col gap-0 overflow-hidden border-blue-900/60',
          'bg-[#0a1048] p-0 text-white shadow-2xl shadow-black/50',
          'sm:rounded-2xl'
        )}
      >
        <DiscoverDialogClose className="right-4 top-4" />

        <DialogHeader className="space-y-3 border-b border-white/[0.08] px-5 py-4 pr-14 text-left">
          <div className="flex items-start gap-4">
            <Image
              src={POWER_TRADE_ICON_SRC}
              alt=""
              width={52}
              height={52}
              className="h-[52px] w-[52px] shrink-0 object-contain"
              aria-hidden
            />
            <div className="min-w-0 space-y-1.5 pt-0.5">
              <DialogTitle className="font-poppins text-lg font-bold text-white">
                Histórico Fairtrade
              </DialogTitle>
              <DialogDescription className="font-inter text-sm text-blue-100/70">
                Mudanças registradas para{' '}
                <span className="font-semibold text-white">{displayName}</span>.
              </DialogDescription>
            </div>
          </div>
          {quote ? <CurrentQuoteCard quote={quote} /> : null}
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-14">
              <Loader2 className="h-6 w-6 animate-spin text-sky-400" />
              <p className="font-poppins text-sm text-white/50">Carregando histórico...</p>
            </div>
          ) : error ? (
            <p className="px-5 py-10 text-center font-inter text-sm text-rose-300/90">{error}</p>
          ) : logs.length === 0 ? (
            <p className="px-5 py-10 text-center font-inter text-sm text-white/50">
              Ainda não há alterações registradas para este power.
            </p>
          ) : (
            <Tabs
              value={tab}
              onValueChange={(v) => setTab(v as 'registros' | 'graficos')}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="border-b border-white/[0.08] px-5 pt-3">
                <TabsList className="h-auto w-full justify-start gap-1 rounded-none bg-transparent p-0">
                  <TabsTrigger
                    value="registros"
                    className={cn(
                      'gap-1.5 rounded-t-lg rounded-b-none border-b-2 border-transparent px-3 py-2.5',
                      'font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/60',
                      'data-[state=active]:border-sky-400 data-[state=active]:bg-sky-500/10 data-[state=active]:text-sky-100 data-[state=active]:shadow-none'
                    )}
                  >
                    <List className="h-3.5 w-3.5" aria-hidden />
                    Registros
                  </TabsTrigger>
                  <TabsTrigger
                    value="graficos"
                    className={cn(
                      'gap-1.5 rounded-t-lg rounded-b-none border-b-2 border-transparent px-3 py-2.5',
                      'font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/60',
                      'data-[state=active]:border-sky-400 data-[state=active]:bg-sky-500/10 data-[state=active]:text-sky-100 data-[state=active]:shadow-none'
                    )}
                  >
                    <LineChart className="h-3.5 w-3.5" aria-hidden />
                    Gráficos
                  </TabsTrigger>
                </TabsList>
              </div>

              <div
                className={cn(
                  'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5',
                  'max-h-[min(58vh,560px)]',
                  '[scrollbar-gutter:stable]',
                  '[scrollbar-width:thin]',
                  '[scrollbar-color:rgba(56,189,248,0.45)_rgba(255,255,255,0.06)]',
                  '[&::-webkit-scrollbar]:w-1.5',
                  '[&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-white/[0.06]',
                  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-sky-400/45',
                  '[&::-webkit-scrollbar-thumb]:hover:bg-sky-300/60'
                )}
              >
                <TabsContent
                  value="registros"
                  className="mt-0 overflow-visible focus-visible:outline-none"
                >
                  {daysEstimated ? (
                    <p className="mb-2.5 font-inter text-[10px] text-blue-100/45">
                      Days estimados pela relação xats/days actual
                    </p>
                  ) : null}
                  <ul className="space-y-2.5">
                    {logs.map((entry) => (
                      <HistoryRow key={`${entry.dateInt}-${entry.newLabel}`} entry={entry} />
                    ))}
                  </ul>
                </TabsContent>

                <TabsContent
                  value="graficos"
                  className="mt-0 overflow-visible focus-visible:outline-none data-[state=inactive]:hidden"
                >
                  <FairtradeHistoryCharts points={chart} daysEstimated={daysEstimated} />
                </TabsContent>
              </div>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
